import React, { useState, useEffect } from 'react';
import { 
  TextField, 
  MenuItem, 
  Select, 
  FormControl, 
  InputLabel, 
  Box, 
  Typography, 
  Button, 
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Paper,
  Divider,
  Tooltip,
  IconButton,
  SelectChangeEvent
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import InfoIcon from '@mui/icons-material/Info';
import { 
  UnitConverterProps, 
  UnitConversion, 
  UnitType, 
  unitCategoryMap, 
  standardConversions
} from '../utils/commonApis';

const UnitConverter: React.FC<UnitConverterProps> = ({
  mode,
  baseUnit,
  baseValue,
  unitConversions = [],
  onChange,
  onAddConversion
}) => {
  const [selectedUnit, setSelectedUnit] = useState<string>(baseUnit);
  const [displayValue, setDisplayValue] = useState<number>(baseValue);
  const [availableUnits, setAvailableUnits] = useState<string[]>([baseUnit]);
  
  // For add mode
  const [newUnit, setNewUnit] = useState<string>('');
  const [conversionFactor, setConversionFactor] = useState<number>(1);
  const [openDialog, setOpenDialog] = useState<boolean>(false);
  const [customUnit, setCustomUnit] = useState<string>('');

  // Initialize available units
  useEffect(() => {
    // Initialize available units
    const baseCategory = unitCategoryMap[baseUnit as UnitType];
    const compatibleUnits = Object.keys(standardConversions[baseCategory]);
    
    // Add custom unit conversions and their compatible units
    const allAvailableUnits = new Set<string>(compatibleUnits);
    
    // For each custom unit conversion, add the unit and its compatible units
    unitConversions.forEach(conversion => {
      // Add the custom unit
      allAvailableUnits.add(conversion.unit);
      
      // Try to identify the category of the custom unit
      const customUnitCategory = unitCategoryMap[conversion.unit as UnitType];
      if (customUnitCategory) {
        // Add all compatible units from the custom unit's category
        Object.keys(standardConversions[customUnitCategory]).forEach(relatedUnit => {
          allAvailableUnits.add(relatedUnit);
        });
      }
    });
    
    setAvailableUnits(Array.from(allAvailableUnits));
  }, [baseUnit, unitConversions]);

  const convertFromBase = (baseVal: number, toUnit: string): number => {
    const baseCategory = unitCategoryMap[baseUnit as UnitType];
    
    // Check if it's a standard compatible unit
    if (standardConversions[baseCategory][toUnit]) {
      const newValue = baseVal * standardConversions[baseCategory][toUnit];
      setDisplayValue(newValue);
      return newValue;
    }
    
    // Check if it's a direct custom unit conversion
    const directCustomConversion = unitConversions.find(conv => conv.unit === toUnit);
    if (directCustomConversion) {
      const newValue = baseVal * directCustomConversion.conversionFactor;
      setDisplayValue(newValue);
      return newValue;
    }
    
    // Check for transitive conversions through custom units
    // Store all possible conversion paths to handle ambiguities
    const transitivePaths = [];
  
    for (const customConv of unitConversions) {
      const customUnitCategory = unitCategoryMap[customConv.unit as UnitType];
    
      // If the custom unit has a category and the toUnit is in that category
      if (customUnitCategory && standardConversions[customUnitCategory][toUnit]) {
        // First convert from base unit to custom unit
        const valueInCustomUnit = baseVal * customConv.conversionFactor;
        // Then convert from custom unit to target unit
        const finalValue = valueInCustomUnit * standardConversions[customUnitCategory][toUnit];
        transitivePaths.push({ value: finalValue, path: `${baseUnit} → ${customConv.unit} → ${toUnit}` });
      }
    }
  
    // If we found any transitive paths, use the most direct one (shortest path)
    if (transitivePaths.length > 0) {
      const bestPath = transitivePaths[0]; // Default to first found path
      setDisplayValue(bestPath.value);
      return bestPath.value;
    }
    
    // Default case: assume it's the base unit
    setDisplayValue(baseVal);
    return baseVal;
  };

  useEffect(() => {
    // Function to update display value
    const updateDisplayValue = () => {
      const baseCategory = unitCategoryMap[baseUnit as UnitType];
      
      // Check if it's a standard compatible unit
      if (standardConversions[baseCategory][selectedUnit]) {
        const newValue = baseValue * standardConversions[baseCategory][selectedUnit];
        setDisplayValue(newValue);
        return;
      }
      
      // Check if it's a direct custom unit conversion
      const directCustomConversion = unitConversions.find(conv => conv.unit === selectedUnit);
      if (directCustomConversion) {
        const newValue = baseValue * directCustomConversion.conversionFactor;
        setDisplayValue(newValue);
        return;
      }
      
      // Check for transitive conversions through custom units
      // Store all possible conversion paths to handle ambiguities
      const transitivePaths = [];
  
      for (const customConv of unitConversions) {
        const customUnitCategory = unitCategoryMap[customConv.unit as UnitType];
    
        // If the custom unit has a category and the selectedUnit is in that category
        if (customUnitCategory && standardConversions[customUnitCategory][selectedUnit]) {
          // First convert from base unit to custom unit
          const valueInCustomUnit = baseValue * customConv.conversionFactor;
          // Then convert from custom unit to target unit
          const finalValue = valueInCustomUnit * standardConversions[customUnitCategory][selectedUnit];
          transitivePaths.push({ value: finalValue, path: `${baseUnit} → ${customConv.unit} → ${selectedUnit}` });
        }
      }
  
      // If we found any transitive paths, use the most direct one
      if (transitivePaths.length > 0) {
        const bestPath = transitivePaths[0]; // Default to first found path
        setDisplayValue(bestPath.value);
        return;
      }
      
      // Default case: assume it's the base unit
      setDisplayValue(baseValue);
    };
    
    updateDisplayValue();
  }, [baseValue, selectedUnit, baseUnit, unitConversions]);

  const convertToBase = (value: number, fromUnit: string): number => {
    const baseCategory = unitCategoryMap[baseUnit as UnitType];
    
    // Check if it's a standard compatible unit for the base unit
    if (standardConversions[baseCategory][fromUnit]) {
      return value / standardConversions[baseCategory][fromUnit];
    }
    
    // Check if it's a custom unit conversion
    const directCustomConversion = unitConversions.find(conv => conv.unit === fromUnit);
    if (directCustomConversion) {
      return value / directCustomConversion.conversionFactor;
    }
    
    // Check for transitive conversions through custom units
    // Store all possible conversion paths to handle ambiguities
    const transitivePaths = [];
  
    for (const customConv of unitConversions) {
      const customUnitCategory = unitCategoryMap[customConv.unit as UnitType];
    
      // If the custom unit has a category and the fromUnit is in that category
      if (customUnitCategory && standardConversions[customUnitCategory][fromUnit]) {
        // First convert from the input unit to the custom unit
        const valueInCustomUnit = value / standardConversions[customUnitCategory][fromUnit];
        // Then convert from custom unit to base unit
        const finalValue = valueInCustomUnit / customConv.conversionFactor;
        transitivePaths.push({ value: finalValue, path: `${fromUnit} → ${customConv.unit} → ${baseUnit}` });
      }
    }
  
    // If we found any transitive paths, use the most direct one
    if (transitivePaths.length > 0) {
      const bestPath = transitivePaths[0]; // Default to first found path
      return bestPath.value;
    }
    
    // Default case: assume it's the base unit
    return value;
  };



  const handleValueChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newDisplayValue = parseFloat(e.target.value);
    setDisplayValue(newDisplayValue);
    
    // Convert to base unit and call onChange
    const newBaseValue = convertToBase(newDisplayValue, selectedUnit);
    if (onChange) {
      onChange(newBaseValue, baseUnit);
    }
  };

  const handleUnitChange = (e: SelectChangeEvent) => {
    const newUnit = e.target.value;
    setSelectedUnit(newUnit);
    
    // Convert the current base value to the newly selected unit
    convertFromBase(baseValue, newUnit);
  };

  const handleOpenDialog = () => {
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setNewUnit('');
    setConversionFactor(1);
    setCustomUnit('');
  };

  const handleAddConversion = () => {
    const unitToAdd = customUnit || newUnit;
    if (unitToAdd && conversionFactor > 0 && onAddConversion) {
      const newConversion: UnitConversion = {
        unit: unitToAdd,
        conversionFactor: conversionFactor
      };
      
      onAddConversion(newConversion);
      handleCloseDialog();
    }
  };

  const renderAddMode = () => (
    <Box>
      <Grid container spacing={2} alignItems="center">
        <Grid item xs={12} sm={6}>
          <Typography variant="body1">Base Unit: {baseUnit}</Typography>
        </Grid>
        <Grid item xs={12} sm={6}>
          <Button 
            variant="contained" 
            color="primary" 
            startIcon={<AddIcon />}
            onClick={handleOpenDialog}
          >
            Add Custom Conversion
          </Button>
        </Grid>
      </Grid>

      <Dialog open={openDialog} onClose={handleCloseDialog}>
        <DialogTitle>Add Custom Unit Conversion</DialogTitle>
        <DialogContent>
          <Box sx={{ p: 1 }}>
            <Typography variant="body2" sx={{ mb: 2 }}>
              Define a conversion from {baseUnit} to a new unit.
              <Tooltip title={`Formula: ${baseUnit}_value = conversionFactor * unitValue. Note: If you add a conversion to a known unit type (like cm), you'll also be able to convert to related units (like mm, m) through transitive conversion.`}>
                <IconButton size="small">
                  <InfoIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Typography>
            
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <FormControl fullWidth sx={{ mb: 2 }}>
                  <InputLabel>Select from known units</InputLabel>
                  <Select
                    value={newUnit}
                    onChange={(e: SelectChangeEvent) => setNewUnit(e.target.value)}
                    label="Select from known units"
                  >
                    <MenuItem value="">
                      <em>None</em>
                    </MenuItem>
                    {Object.keys(unitCategoryMap).map((unit) => (
                      unit !== baseUnit && (
                        <MenuItem key={unit} value={unit}>
                          {unit}
                        </MenuItem>
                      )
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              
              <Grid item xs={12}>
                <Typography variant="body2">OR</Typography>
              </Grid>
              
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Custom Unit Name"
                  value={customUnit}
                  onChange={(e) => setCustomUnit(e.target.value)}
                  sx={{ mb: 2 }}
                />
              </Grid>
              
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  type="number"
                  label="Conversion Factor"
                  value={conversionFactor}
                  onChange={(e) => setConversionFactor(parseFloat(e.target.value))}
                  helperText={`1 ${baseUnit} = ${conversionFactor} ${newUnit || customUnit || 'new unit'}`}
                />
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button 
            onClick={handleAddConversion} 
            color="primary" 
            disabled={(!newUnit && !customUnit) || conversionFactor <= 0}
          >
            Add Conversion
          </Button>
        </DialogActions>
      </Dialog>

      <Paper elevation={3} sx={{ p: 2, mt: 2 }}>
        <Typography variant="h6">Available Unit Conversions</Typography>
        <Divider sx={{ my: 1 }} />
        
        {unitConversions.length > 0 ? (
          <Grid container spacing={2}>
            {unitConversions.map((conversion, index) => (
              <Grid item xs={12} sm={6} md={4} key={index}>
                <Paper elevation={1} sx={{ p: 1 }}>
                  <Typography variant="body1">
                    1 {baseUnit} = {conversion.conversionFactor} {conversion.unit}
                  </Typography>
                </Paper>
              </Grid>
            ))}
          </Grid>
        ) : (
          <Typography variant="body2" color="textSecondary">
            No custom conversions added yet
          </Typography>
        )}
      </Paper>
    </Box>
  );

  const renderConvertMode = () => (
    <Box>
      <Grid container spacing={2} alignItems="center">
        <Grid item xs={12} sm={5}>
          <TextField
            fullWidth
            label="Value"
            type="number"
            value={displayValue}
            onChange={handleValueChange}
            variant="outlined"
          />
        </Grid>
        <Grid item xs={12} sm={5}>
          <FormControl fullWidth>
            <InputLabel>Unit</InputLabel>
            <Select
              value={selectedUnit}
              onChange={handleUnitChange}
              label="Unit"
            >
              {availableUnits.map((unit) => (
                <MenuItem key={unit} value={unit}>
                  {unit}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} sm={2}>
          <Typography variant="body2" color="textSecondary">
            Base: {baseValue.toFixed(4)} {baseUnit}
          </Typography>
        </Grid>
      </Grid>
    </Box>
  );

  return (
    <Box sx={{ width: '100%' }}>
      {mode === 'add' ? renderAddMode() : renderConvertMode()}
    </Box>
  );
};

export default UnitConverter;